import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Button,
    Grid,
    Box,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Chip,
    Avatar,
    IconButton,
    Fade,
    Container,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { Public } from "@mui/icons-material";
import { CalendarToday } from "@mui/icons-material";
import {
    Person,
    Work,
    Download,
    CheckCircle,
    RadioButtonUnchecked,
    Send,
} from "@mui/icons-material";
import Navbar from "../../Navbar/Navbar";
import baseURL from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Crewcvs = () => {
    const [crewCVs, setCrewCVs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [approvedCVs, setApprovedCVs] = useState(new Set());
    const [selectedCVs, setSelectedCVs] = useState(new Set());
    const [vesselOwners, setVesselOwners] = useState([]);
    const [selectedVesselOwners, setSelectedVesselOwners] = useState([]);
    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [assignLoading, setAssignLoading] = useState(false);

    useEffect(() => {
        const fetchApprovedCrewCVs = async () => {
            const token = localStorage.getItem("token");
            try {
                const res = await axios.get(
                    `${baseURL}/api/crewCVuploadByCrewingAgentRoute/all`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                const cvData = res.data.data || [];

                if (cvData.length > 0 && cvData[0].cvs && cvData[0].cvs.length > 0) {
                    // console.log("Sample CV structure:", cvData[0].cvs[0]);
                }

                const totalApproved = crewCVs.reduce((acc, crew) => {
                    return acc + (crew.cvs?.filter(cv => cv.approved)?.length || 0);
                }, 0) + approvedCVs.size;

                setCrewCVs(cvData);
            } catch (error) {
                console.error("Error fetching approved crew CVs:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchApprovedCrewCVs();
    }, []);

    useEffect(() => {
        const fetchVesselOwners = async () => {
            const token = localStorage.getItem("token");
            try {
                // Replace with your actual vessel owners endpoint
                const res = await axios.get(
                    `${baseURL}/api/vesselOwner/getVesselOwnerALL`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                setVesselOwners(res.data.data || []);
            } catch (error) {
                console.error("Error fetching vessel owners:", error);
                toast.error("Failed to fetch vessel owners");
            }
        };

        fetchVesselOwners();
    }, []);

    const handleApprovalChange = async (cvId, isApproved, actualCvId) => {
        if (isApproved) {
            try {
                const token = localStorage.getItem("token");

                const response = await axios.put(
                    `${baseURL}/api/crewCVuploadByCrewingAgentRoute/${actualCvId}/approve`,
                    { isApproved: true },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                    }
                );

                if (response.status === 200) {
                    setApprovedCVs(prev => {
                        const newSet = new Set(prev);
                        newSet.add(cvId);
                        return newSet;
                    });
                    window.location.reload();

                    toast.success("CV approved successfully!");
                }
            } catch (error) {
                console.error("Error approving CV:", error);
                const errorMessage = error.response?.data?.message || "Failed to approve CV. Please try again.";
                toast.error(errorMessage);
            }
        } else {
            setApprovedCVs(prev => {
                const newSet = new Set(prev);
                newSet.delete(cvId);
                return newSet;
            });
        }
    };

    const handleCVSelection = (cvId, checked) => {
        setSelectedCVs(prev => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(cvId);
            } else {
                newSet.delete(cvId);
            }
            return newSet;
        });
    };

    const handleOpenAssignDialog = () => {
        if (selectedCVs.size === 0) {
            toast.warning("Please select at least one CV to assign");
            return;
        }
        setOpenAssignDialog(true);
    };

    const handleCloseAssignDialog = () => {
        setOpenAssignDialog(false);
        setSelectedVesselOwners([]);
    };

    const handleAssignCVs = async () => {
        if (selectedVesselOwners.length === 0) {
            toast.warning("Please select at least one vessel owner");
            return;
        }

        setAssignLoading(true);
        const token = localStorage.getItem("token");

        try {
            const cvIds = Array.from(selectedCVs);

            const payload = {
                crewIds: cvIds,
                vesselOwnerIds: selectedVesselOwners
            };

            const response = await axios.post(
                `${baseURL}/api/crewCvAssigned/assign-cv`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                }
            );

            // Success (any 2xx): show toast, clear selections and close modal
            toast.success(response.data?.message || "CVs assigned successfully!");
            setSelectedCVs(new Set());
            setSelectedVesselOwners([]);

            // Close modal after a short delay so the toast is visible
            setTimeout(() => {
                handleCloseAssignDialog();
            }, 800);

        } catch (error) {
            console.error("❌ Error assigning CVs:", error);
            const errorMessage =
                error.response?.data?.message || "Failed to assign CVs. Please try again.";
            toast.error(errorMessage);
        } finally {
            setAssignLoading(false);
        }
    };


    if (loading)
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="60vh"
                sx={{
                    backgroundColor: '#f8fafc',
                }}
            >
                <CircularProgress
                    size={50}
                    thickness={4}
                    sx={{
                        color: '#3b82f6',
                        mb: 3,
                    }}
                />
                <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 500 }}>
                    Loading crew CVs...
                </Typography>
            </Box>
        );

    if (crewCVs.length === 0)
        return (
            <>
                <Navbar />
                <Container maxWidth="lg" sx={{ py: 4 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            backgroundColor: 'white',
                            borderRadius: 4,
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 80,
                                height: 80,
                                mx: 'auto',
                                mb: 3,
                                bgcolor: '#3b82f6',
                            }}
                        >
                            <Person sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Typography ovedvariant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                            No CVs Available
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', maxWidth: 400, mx: 'auto' }}>
                            There are currently no crew CVs to display. Please check back later or contact your administrator.
                        </Typography>
                    </Paper>
                </Container>
            </>
        );

    const totalApproved =
        crewCVs.reduce((acc, crew) => {
            return acc + (crew.cvs?.filter(cv => cv.approved)?.length || 0);
        }, 0) + approvedCVs.size;

    return (
        <>
            <Navbar />
            <Box
                sx={{
                    minHeight: "100vh",
                    backgroundColor: "#ffffff",
                    py: 4,
                }}
            >
                <Container maxWidth="lg">
                    <Paper
                        elevation={1}
                        sx={{
                            p: 4,
                            mb: 4,
                            borderRadius: 3,
                            background: "white",
                            border: "1px solid #e5e7eb",
                        }}
                    >
                        <Typography
                            variant="h4"
                            gutterBottom
                            sx={{
                                fontWeight: 700,
                                color: "#1e293b",
                                mb: 2,
                            }}
                        >
                            Crew CVs
                        </Typography>

                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                            <Chip
                                icon={<Person />}
                                label={`Total CVs: ${crewCVs.reduce(
                                    (acc, crew) => acc + (crew.cvs?.length || 0),
                                    0
                                )}`}
                                sx={{
                                    fontWeight: 600,
                                    backgroundColor: "#f0f9ff",
                                    color: "#0369a1",
                                    "& .MuiChip-icon": {
                                        color: "#0284c7",
                                    },
                                }}
                            />
                            <Chip
                                icon={<CheckCircle />}
                                label={`Approved: ${totalApproved}`}
                                sx={{
                                    fontWeight: 600,
                                    backgroundColor: "#ecfdf5",
                                    color: "#047857",
                                    "& .MuiChip-icon": {
                                        color: "#059669",
                                    },
                                }}
                            />
                            <Chip
                                label={`Selected: ${selectedCVs.size}`}
                                sx={{
                                    fontWeight: 600,
                                    backgroundColor: "#fef3c7",
                                    color: "#92400e",
                                }}
                            />
                            {selectedCVs.size > 0 && (
                                <Button
                                    variant="contained"
                                    startIcon={<Send />}
                                    onClick={handleOpenAssignDialog}
                                    sx={{
                                        ml: "auto",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        backgroundColor: "#3b82f6",
                                        "&:hover": {
                                            backgroundColor: "#2563eb",
                                        },
                                    }}
                                >
                                    Assign to Vessel Owner
                                </Button>
                            )}
                        </Box>
                    </Paper>

                    <Grid container spacing={3}>
                        {crewCVs.map((crew, i) =>
                            crew.cvs?.map((cv, j) => (
                                <Grid item xs={12} sm={6} md={3} key={`${i}-${j}`}>
                                    {console.log("cv check", cv)}
                                    <Fade in={true} timeout={300 + i * j * 100}>
                                        <Card
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                borderRadius: 3,
                                                background: "white",
                                                border: approvedCVs.has(
                                                    `${i}-${j}`
                                                )
                                                    ? "2px solid #22c55e"
                                                    : selectedCVs.has(cv._id)
                                                        ? "2px solid #3b82f6"
                                                        : "1px solid #e5e7eb",
                                                boxShadow:
                                                    "0 2px 8px rgba(0,0,0,0.05)",
                                                "&:hover": {
                                                    transform: "translateY(-2px)",
                                                    boxShadow:
                                                        "0 8px 20px rgba(0,0,0,0.1)",
                                                },
                                                transition:
                                                    "all 0.25s ease-in-out",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    borderRadius: "12px 12px 0 0",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    height="220"
                                                    image={
                                                        cv?.profilepicture?.filePath
                                                            ? `${baseURL}/${cv.profilepicture.filePath.replace(
                                                                /\\/g,
                                                                "/"
                                                            )}`
                                                            : "https://via.placeholder.com/200x220?text=No+Image"
                                                    }
                                                    alt={cv?.name || "No Name"}
                                                    sx={{
                                                        objectFit: "contain",
                                                        backgroundColor: "#ffffff",
                                                        width: "100%",

                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: 12,
                                                        right: 12,
                                                    }}
                                                >
                                                    {cv.approved ? (
                                                        <Chip
                                                            label="Approved"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    "#22c55e",
                                                                color: "white",
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                background:
                                                                    "rgba(255,255,255,0.9)",
                                                                borderRadius: "50%",
                                                                boxShadow: 1,
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={approvedCVs.has(
                                                                    `${i}-${j}`
                                                                )}
                                                                onChange={(e) =>
                                                                    handleApprovalChange(
                                                                        `${i}-${j}`,
                                                                        e.target
                                                                            .checked,
                                                                        cv._id
                                                                    )
                                                                }
                                                                icon={
                                                                    <RadioButtonUnchecked />
                                                                }
                                                                checkedIcon={
                                                                    <CheckCircle />
                                                                }
                                                                sx={{
                                                                    color: "#22c55e",
                                                                    "&.Mui-checked":
                                                                    {
                                                                        color: "#22c55e",
                                                                    },
                                                                    p: 0.5,
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>

                                            <CardContent
                                                sx={{ flexGrow: 1, p: 3 }}
                                            >
                                                {cv?.name && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            mb: 2,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                mr: 1.5,
                                                                color: "red",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            Name:
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: "#1e293b",
                                                            }}
                                                        >
                                                            {cv?.name}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {cv?.rank && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            mb: 3,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                mr: 1.5,
                                                                color: "red",
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            Rank:
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: "#000",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {cv?.rank}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {cv?.nationality && (
                                                    <Box
                                                        sx={{ display: "flex", alignItems: "center", mb: 3, }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                mr: 1.5,
                                                                color: "red",
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            Nationality:
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: "#000",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {cv?.nationality}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {cv?.birthdate && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            mb: 3,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                mr: 1.5,
                                                                color: "red",
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            BirthDate:
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: "#000",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {cv?.birthdate &&
                                                                new Date(cv.birthdate).toLocaleDateString("en-GB")
                                                                    .replace(/\//g, "-")}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                <Button
                                                    variant="contained"
                                                    fullWidth
                                                    startIcon={<Download />}
                                                    href={`${baseURL}/${cv?.filePath?.replace(
                                                        /\\/g,
                                                        "/"
                                                    )}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        py: 1.5,
                                                        borderRadius: 2,
                                                        textTransform: "none",
                                                        fontWeight: 600,
                                                        fontSize: "0.9rem",
                                                        backgroundColor: "#C30B12",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "#C30B12",
                                                        },
                                                        mb: 2,
                                                    }}
                                                >
                                                    View CV
                                                </Button>

                                                {cv?.approved && (<FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={selectedCVs.has(cv._id)}
                                                            onChange={(e) =>
                                                                handleCVSelection(
                                                                    cv._id,
                                                                    // crew._id,
                                                                    e.target.checked
                                                                )
                                                            }
                                                            sx={{
                                                                color: "#3b82f6",
                                                                "&.Mui-checked": {
                                                                    color: "#3b82f6",
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: "#64748b",
                                                            }}
                                                        >
                                                            Share CV with Owner
                                                        </Typography>
                                                    }
                                                    sx={{ m: 0 }}
                                                />
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Fade>
                                </Grid>
                            ))
                        )}
                    </Grid>
                </Container>
            </Box>

            {/* Assignment Dialog */}
            <Dialog
                open={openAssignDialog}
                onClose={handleCloseAssignDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, color: "#1e293b" }}>
                    Assign CVs to Vessel Owner
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: "#64748b" }}>
                            You have selected {selectedCVs.size} CV(s) to assign.
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel>Select Vessel Owners</InputLabel>
                            <Select
                                multiple
                                value={selectedVesselOwners}
                                onChange={(e) => setSelectedVesselOwners(e.target.value)}
                                label="Select Vessel Owners"
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const owner = vesselOwners.find(o => o._id === value);
                                            return (
                                                <Chip
                                                    key={value}
                                                    label={owner?.company_name || owner?.companyName || 'Unknown'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#e0f2fe',
                                                        color: '#0369a1',
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {vesselOwners.map((owner) => (
                                    <MenuItem key={owner._id} value={owner._id}>
                                        <Checkbox
                                            checked={selectedVesselOwners.indexOf(owner._id) > -1}
                                            sx={{
                                                color: "#3b82f6",
                                                "&.Mui-checked": {
                                                    color: "#3b82f6",
                                                },
                                            }}
                                        />
                                        <Typography>{owner?.company_name || owner.companyName}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2 }}>
                    <Button
                        onClick={handleCloseAssignDialog}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#64748b",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAssignCVs}
                        variant="contained"
                        disabled={assignLoading || selectedVesselOwners.length === 0}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            backgroundColor: "#3b82f6",
                            "&:hover": {
                                backgroundColor: "#2563eb",
                            },
                        }}
                    >
                        {assignLoading ? (
                            <CircularProgress size={24} sx={{ color: "white" }} />
                        ) : (
                            "Assign"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <ToastContainer />
        </>
    );
};

export default Crewcvs;